import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { User, Bell, Database, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { isNative } from '../utils/native';
import { PushToggle } from '../components/common/PushCard';
import { BiometricToggle } from '../components/common/BiometricToggle';

// Billing moved to its own page in the main menu — see pages/Billing.js.

export const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [pendingAccess, setPendingAccess] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('support_access')
      .select('id', { count: 'exact', head: true })
      .eq('owner_id', user.id).eq('status', 'pending')
      .then(({ count }) => setPendingAccess(count || 0));
  }, [user?.id, activeTab]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [password, setPassword] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({});
  const [fullName, setFullName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [v, i, r, m, s, e] = await Promise.all([
        supabase.from('vehicles').select('id', { count: 'exact' }),
        supabase.from('insurance_records').select('id', { count: 'exact' }),
        supabase.from('road_tax_records').select('id', { count: 'exact' }),
        supabase.from('mechanical_reports').select('id', { count: 'exact' }),
        supabase.from('service_records').select('id', { count: 'exact' }),
        supabase.from('expenses').select('id', { count: 'exact' }),
      ]);
      setStats({
        vehicles: v.count || 0,
        insurance: i.count || 0,
        roadTax: r.count || 0,
        mechanical: m.count || 0,
        services: s.count || 0,
        expenses: e.count || 0,
      });
    };
    load();
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase.from('user_profiles').select('full_name').eq('id', user.id).single().then(({ data }) => {
      if (data?.full_name) setFullName(data.full_name);
    });
  }, [user]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (password.new !== password.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (password.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: password.new });
    if (error) toast.error(error.message);
    else { toast.success('Password updated!'); setPassword({ current: '', new: '', confirm: '' }); }
    setSaving(false);
  };

  const saveName = async () => {
    setSavingName(true);
    await supabase.from('user_profiles').upsert({
      id: user.id,
      full_name: fullName,
      updated_at: new Date().toISOString(),
    });
    toast.success('Name saved!');
    setSavingName(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Manage your account and preferences</div>
        </div>
      </div>

      <div className="tabs">
        {[
          {
            key: 'profile',
            label: 'Profile',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
          },
          {
            key: 'notifications',
            label: 'Notifications',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
          },
          {
            key: 'data',
            label: 'Data & Stats',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
          },
          {
            key: 'support',
            label: 'Support Access',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
          },
        ].map(t => (
          <div key={t.key} className={`tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.icon}{t.label}
            {t.key === 'support' && pendingAccess > 0 && (
              <span style={{
                marginLeft: 6, minWidth: 17, height: 17, padding: '0 5px', borderRadius: 9,
                background: '#f97316', color: '#fff', fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{pendingAccess}</span>
            )}
          </div>
        ))}
      </div>

      {activeTab === 'profile' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {isNative && <BiometricToggle />}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <User size={16} style={{ color: 'var(--accent)' }} />
              <span style={{ fontWeight: 700 }}>Account Information</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  className="form-input"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="e.g. Alexis"
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingName}
                  onClick={saveName}
                >
                  {savingName ? 'Saving…' : 'Save Name'}
                </button>
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={user?.email || ''} disabled style={{ opacity: 0.6 }} />
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>Email cannot be changed here. Contact support if needed.</span>
              </div>
              <div className="form-group">
                <label className="form-label">User ID</label>
                <input className="form-input" value={user?.id || ''} disabled style={{ opacity: 0.6, fontFamily: 'DM Mono,monospace', fontSize: 12 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Account Created</label>
                <input className="form-input" value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''} disabled style={{ opacity: 0.6 }} />
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <Shield size={16} style={{ color: 'var(--accent)' }} />
              <span style={{ fontWeight: 700 }}>Change Password</span>
            </div>
            <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="password" value={password.new} onChange={e => setPassword(p => ({ ...p, new: e.target.value }))} placeholder="Min. 6 characters" required />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input className="form-input" type="password" value={password.confirm} onChange={e => setPassword(p => ({ ...p, confirm: e.target.value }))} placeholder="Repeat new password" required />
              </div>
              <div>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Updating…' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'notifications' && isNative && <PushToggle />}
      {activeTab === 'notifications' && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Bell size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontWeight: 700 }}>Alert Thresholds</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Critical alert threshold', value: '7 days', desc: 'Show red badge when expiry is within this many days' },
              { label: 'Warning alert threshold', value: '30 days', desc: 'Show orange badge when expiry is within this many days' },
              { label: 'Upcoming alert threshold', value: '60 days', desc: 'Show yellow badge when expiry is within this many days' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--surface2)', borderRadius: 8, gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.label}</div>
                  <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 2 }}>{item.desc}</div>
                </div>
                <span className="badge badge-blue">{item.value}</span>
              </div>
            ))}
            <div style={{ padding: '16px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Bell size={14} style={{ color: 'var(--accent)' }} />
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Email Notifications</span>
                <span className="badge badge-green" style={{ marginLeft: 'auto' }}>Active</span>
              </div>
              <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14 }}>
                Sends an email alert when any vehicle has expired or critical documents (within 7 days).
                Covers insurance, road tax, mechanical reports, service records and documents.
              </p>

              {/* Alert thresholds info */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { label: 'Expired', color: 'var(--red)' },
                  { label: '1-7 days', color: 'var(--orange)' },
                ].map(item => (
                  <span key={item.label} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${item.color}22`, color: item.color, border: `1px solid ${item.color}44` }}>
                    {item.label}
                  </span>
                ))}
              </div>

              {/* Manual trigger */}
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                disabled={emailSending}
                onClick={async () => {
                  setEmailSending(true);
                  setEmailResult(null);
                  try {
                    const { data, error } = await supabase.functions.invoke('send-fleet-alerts');
                    if (error) { setEmailResult({ success: false, message: error.message }); return; }
                    if (data.sent) {
                      setEmailResult({ success: true, message: `✓ Email sent with ${data.alertCount} alert${data.alertCount !== 1 ? 's' : ''}` });
                    } else {
                      setEmailResult({ success: true, message: '✓ No alerts to send — your fleet is in good shape!' });
                    }
                  } catch (err) {
                    setEmailResult({ success: false, message: err.message });
                  }
                  setEmailSending(false);
                }}
              >
                <Bell size={14} />
                {emailSending ? 'Sending…' : 'Send Alert Email Now'}
              </button>

              {emailResult && (
                <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: emailResult.success ? 'var(--green-bg)' : 'var(--red-bg)', color: emailResult.success ? 'var(--green)' : 'var(--red)', border: `1px solid ${emailResult.success ? 'var(--green)' : 'var(--red)'}33` }}>
                  {emailResult.message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'data' && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Database size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontWeight: 700 }}>Database Statistics</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {[
              ['Vehicles', stats.vehicles],
              ['Insurance Records', stats.insurance],
              ['Road Tax Records', stats.roadTax],
              ['Mechanical Reports', stats.mechanical],
              ['Service Records', stats.services],
              ['Expense Entries', stats.expenses],
            ].map(([label, count]) => (
              <div key={label} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', letterSpacing: -1 }}>{count ?? '—'}</div>
                <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Supabase Storage</div>
            <p style={{ fontSize: 14, color: 'var(--text2)' }}>
              All uploaded files (insurance documents, invoices, receipts, photos) are stored securely in your Supabase Storage bucket <code style={{ background: 'var(--surface)', padding: '1px 5px', borderRadius: 4 }}>vehicle-files</code>. You can manage files directly in your Supabase dashboard.
            </p>
          </div>
        </div>
      )}
      {activeTab === 'support' && (
        <div>
          <SupportAccessSection user={user} />
        </div>
      )}
    </div>
  );
};

// ── Support Access ────────────────────────────────────────────────────────────
// Nobody at Car Collector Studio can see this account's data without the owner approving it
// here first. Approvals expire on their own; the owner can revoke at any time.
const SupportAccessSection = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!user?.id) return;
    const [{ data: reqs }, { data: log }] = await Promise.all([
      supabase.from('support_access').select('*')
        .eq('owner_id', user.id).order('requested_at', { ascending: false }).limit(20),
      supabase.from('admin_audit_log').select('*')
        .eq('target_user_id', user.id).order('created_at', { ascending: false }).limit(20),
    ]);
    setRequests(reqs || []);
    setAuditLog(log || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (req, decision) => {
    const verb = { active: 'approve', denied: 'deny', revoked: 'revoke' }[decision];
    if (!window.confirm(`Are you sure you want to ${verb} this support access request?`)) return;
    setBusy(req.id);
    const patch = decision === 'active'
      ? { status: 'active', approved_at: new Date().toISOString() }
      : decision === 'revoked'
        ? { status: 'revoked', revoked_at: new Date().toISOString() }
        : { status: 'denied' };
    const { error } = await supabase.from('support_access').update(patch).eq('id', req.id);
    setBusy(null);
    if (error) { toast.error('Could not update: ' + error.message); return; }
    toast.success(decision === 'active' ? 'Support access granted' :
                  decision === 'revoked' ? 'Access revoked' : 'Request denied');
    load();
  };

  const isLive = r => r.status === 'active' && !r.revoked_at && new Date(r.expires_at) > new Date();
  const pending = requests.filter(r => r.status === 'pending');
  const active  = requests.filter(isLive);
  const past    = requests.filter(r => !isLive(r) && r.status !== 'pending');

  const fmtDate = d => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  if (loading) return <div className="card"><div style={{ padding: 24, color: 'var(--text3)' }}>Loading…</div></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Support access</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
          Car Collector Studio support cannot view your fleet data unless you grant access here. Approvals
          expire automatically, and you can revoke them at any time. Every time your data is
          accessed it is recorded below.
        </div>
      </div>

      {pending.length > 0 && (
        <div className="card" style={{ border: '1px solid rgba(249,115,22,0.4)' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: '#f97316' }}>
            ⚠ {pending.length} access request{pending.length !== 1 ? 's' : ''} awaiting your approval
          </div>
          {pending.map(r => (
            <div key={r.id} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                <strong>Reason:</strong> {r.reason || 'No reason given'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                Requested {fmtDate(r.requested_at)} · would expire {fmtDate(r.expires_at)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" disabled={busy === r.id}
                  onClick={() => respond(r, 'active')}>Approve</button>
                <button className="btn btn-ghost btn-sm" disabled={busy === r.id}
                  onClick={() => respond(r, 'denied')}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Active access</div>
        {active.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            No one currently has access to your account.
          </div>
        ) : active.map(r => (
          <div key={r.id} style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>
                  Support access is active
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Expires {fmtDate(r.expires_at)}{r.reason ? ` · ${r.reason}` : ''}
                </div>
              </div>
              <button className="btn btn-danger btn-sm" disabled={busy === r.id}
                onClick={() => respond(r, 'revoked')}>Revoke now</button>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Access history</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
          Everything Car Collector Studio support has done on your account.
        </div>
        {auditLog.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Nothing recorded yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Action</th><th>By</th></tr></thead>
              <tbody>
                {auditLog.map(e => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.created_at)}</td>
                    <td>{String(e.action || '').replace(/_/g, ' ')}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{e.admin_email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {past.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
            {past.length} previous request{past.length !== 1 ? 's' : ''} (expired, denied or revoked).
          </div>
        )}
      </div>
    </div>
  );
};